import Icon from '../Icon';
import {
    faPlus as add,
    faCheckCircle as checkmarkCircle,
    faTimes as close,
    faArchive,
    faSearch as search
} from '@fortawesome/free-solid-svg-icons';
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Badge, Modal, ModalBody, Button, ListGroup, InputGroup, Form } from "react-bootstrap";
import { emptyTask, emptyPlan } from "@/state/reducer";
import { format } from "date-fns";
import { ru } from "date-fns/locale";
import { PlanScreenContext } from "./context";
import { useData } from "@/storage/accessors";
import { useLicense } from "@/hooks/useLicense";
import { useNavigate } from "react-router";
import { useTaskModal } from "../TaskModalContext";

export const SelectTasksModal: React.FC<{ isOpen: boolean, toggle: () => void, date: Date }> = ({ isOpen, toggle, date }) => {
    const [filter, setFilter] = useState('');
    const screenContext = useContext(PlanScreenContext);
    const taskModalContext = useTaskModal();
    const setPlanTaskModal = screenContext?.setTaskModal;
    const setTaskModal = taskModalContext?.setTaskModal;
    const ref = useRef<HTMLInputElement>(null);
    const { getLimit } = useLicense();
    const navigate = useNavigate();

    const openTaskModal = (taskUuid: string, fromDate?: Date) => {
        if (setTaskModal) {
            setTaskModal({ taskUUID: taskUuid, fromDate });
        } else if (setPlanTaskModal) {
            setPlanTaskModal({ taskUUID: taskUuid, fromDate });
        }
    };

    // Получаем данные из IndexedDB
    const { roles: parts, tasks, plans } = useData();


    // Вычисляемые данные
    const dateStr = format(date, "yyyy-MM-dd");
    const dayPlans = Object.values(plans.objects).filter(p => p.date === dateStr);
    const dayPlansUuids = dayPlans.map(p => p.task);

    const checkTasksLimit = (): boolean => {
        const tasksCount = Object.values(tasks.objects).length;
        const tasksLimit = getLimit('limit_tasks');

        if (tasksCount >= tasksLimit) {
            navigate('/my/tariffs?reason=limit_tasks');
            return false;
        }
        return true;
    };

    useEffect(() => {
        if (isOpen && ref.current)
            ref.current.focus();
    }, [isOpen]);

    return <Modal show={isOpen} onHide={toggle} >
        <Modal.Header closeButton>Добавить задачу в план на {format(date, "do", { locale: ru })} число</Modal.Header>
        <Modal.Body>
            <Form.Group>
                <InputGroup>
                    <InputGroup.Text><Icon icon={search} /></InputGroup.Text>
                    <Form.Control className="" type="text" value={filter} ref={ref} onChange={e => setFilter(e.target.value)} />
                    <Button onClick={() => setFilter('')}><Icon icon={close} /></Button>
                </InputGroup>
            </Form.Group>
        </Modal.Body>
        <ListGroup variant="flush">
            {filter.length >= 3 ? Object.values(parts.objects)
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .filter(p => screenContext.filterPart ? p.uuid === screenContext.filterPart : true)
                .map(part => {
                    // Фильтруем задачи: принадлежат части, не завершены, еще не в плане
                    let partTasks = Object.values(tasks.objects).filter(t =>
                        t.lifepart === part.uuid &&
                        !t.isDone && !dayPlansUuids.includes(t.uuid)
                    );
                    console.log('pt', partTasks);

                    if (filter !== '')
                        partTasks = partTasks.filter(g => (g.description?.toLocaleLowerCase() || "").indexOf(filter.toLocaleLowerCase()) >= 0);

                    partTasks.sort((a, b) => (a.order || 0) - (b.order || 0));
                    return partTasks.map(task => {

                        return <ListGroup.Item
                            key={`g-${task.uuid}:${part.uuid}`}
                            className="d-flex justify-content-between align-items-center"
                        >
                            <span
                                className="flex-grow-1"
                                style={{ cursor: "pointer" }}
                                onClick={() => {
                                    if (!task) return;
                                    // Создаем план
                                    plans.create(emptyPlan(task, date, 0));
                                    toggle();
                                }}
                            >
                                <Badge bg="" style={{ backgroundColor: part.color, width: 20, marginRight: 10 }} >&nbsp;</Badge>
                                {task.description}
                            </span>
                            <div>
                                <Button size="sm" variant="outline-secondary" title="Архивировать" onClick={() => {
                                    if (!task) return;
                                    // Просто отмечаем задачу выполненной (не добавляя в план)
                                    tasks.save(task.uuid, { isDone: true });
                                }}><Icon icon={faArchive}></Icon></Button>
                            </div>
                        </ListGroup.Item>
                    })
                }) : (
                <ListGroup.Item className="text-center text-muted py-3">
                    <small>Начните вводить название задачи (минимум 3 символа)</small>
                </ListGroup.Item>
            )}
        </ListGroup>
        {filter.length >= 3 && isOpen && <ModalBody>
            <h5>Создать новую задачу</h5>
            <span><small>Выберете к чему относиться новая задача</small></span>
            {Object.values(parts.objects)
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map(part =>
                    <div key={part.uuid} className="mb-2">
                        <Button
                            variant="outline-primary"
                            key={`${part.uuid}`}
                            onClick={() => {
                                if (!part) return;
                                if (!checkTasksLimit()) return;

                                const newTask = emptyTask(part, filter);
                                tasks.create(newTask);
                                plans.create(emptyPlan(newTask, date, dayPlans.length));
                                setFilter("");
                                toggle();
                                openTaskModal(newTask.uuid, date);
                            }}
                        >
                            <Badge bg="" style={{ backgroundColor: part.color, width: 20, marginRight: 10 }}>&nbsp;</Badge>
                            {part.name}
                        </Button>
                        <br />
                    </div>
                )}
        </ModalBody>}
    </Modal>
}

export const TaskInputButton: React.FC<{ date: Date, disabled?: boolean }> = ({ date, disabled }) => {
    const [modalOpen, setModalOpen] = useState(false);
    const toggle = useCallback(() => setModalOpen((prevState) => !prevState), []);
    return <>
        <Button size="sm" variant={disabled ? "outline-secondary" : "outline-primary"} disabled={disabled} onClick={toggle}><Icon icon={add} /></Button>
        {modalOpen && <SelectTasksModal isOpen={modalOpen} toggle={toggle} date={date} />}
    </ >;
};

export const TaskInput: React.FC<{ date: Date }> = ({ date }) => {
    const [modalOpen, setModalOpen] = useState(false);
    const toggle = () => setModalOpen((prevState) => !prevState);
    return <li key={`new-task-input`} className="list-group-item d-flex align-items-center justify-content-center" style={{ cursor: "pointer" }}><div className="d-flex">
        <Button variant="outline-primary" onClick={() => setModalOpen(true)}><Icon icon={add} /> Добавить задачу</Button>
        {modalOpen && <SelectTasksModal isOpen={modalOpen} toggle={toggle} date={date} />}
    </div></li >;
};